import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { inferRouterOutputs } from '@trpc/server';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import Alert from '@/components/UI/Alert.tsx';
import Card from '@/components/UI/Card.tsx';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import type { AppRouter } from '@/types/appRouter';
import { useAppQuery } from '@/hooks/useAppQuery';
import TradeLogicExecutionJournalCard from '@/components/TradeLogic/TradeLogicExecutionJournalCard.tsx';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type TradeLogicExecutionJournalIndexRecord = RouterOutputs['strategy']['fetchTradeLogicExecutionJournalIndex']['journals'][number];
type JournalKindFilter = 'all' | 'entry' | 'exit';
type JournalPhaseFilter = 'all' | 'arbitration' | 'execution';
type JournalStatusFilter = 'all' | 'ready' | 'suppressed' | 'executed' | 'failed';
type JournalLimitFilter = 50 | 100 | 200;

const kindFilterOptions: { value: JournalKindFilter; label: string }[] = [
    { value: 'all', label: 'All logic kinds' },
    { value: 'entry', label: 'Entry logic' },
    { value: 'exit', label: 'Exit logic' }
];

const phaseFilterOptions: { value: JournalPhaseFilter; label: string }[] = [
    { value: 'all', label: 'All phases' },
    { value: 'arbitration', label: 'Arbitration' },
    { value: 'execution', label: 'Execution' }
];

const statusFilterOptions: { value: JournalStatusFilter; label: string }[] = [
    { value: 'all', label: 'All statuses' },
    { value: 'ready', label: 'Ready' },
    { value: 'suppressed', label: 'Suppressed' },
    { value: 'executed', label: 'Executed' },
    { value: 'failed', label: 'Failed' }
];

const limitFilterOptions: { value: JournalLimitFilter; label: string }[] = [
    { value: 50, label: '50 rows' },
    { value: 100, label: '100 rows' },
    { value: 200, label: '200 rows' }
];

const parsePositiveInt = (value: string | null): number | null => {
    if (!value) {
        return null;
    }

    const parsedValue = Number.parseInt(value, 10);

    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
        return null;
    }

    return parsedValue;
};

const parseKindFilter = (value: string | null): JournalKindFilter => {
    if (value === 'entry' || value === 'exit') {
        return value;
    }

    return 'all';
};

const parsePhaseFilter = (value: string): JournalPhaseFilter => {
    if (value === 'arbitration' || value === 'execution') {
        return value;
    }

    return 'all';
};

const parseStatusFilter = (value: string): JournalStatusFilter => {
    if (value === 'ready' || value === 'suppressed' || value === 'executed' || value === 'failed') {
        return value;
    }

    return 'all';
};

const parseEntityType = (value: string | null): 'Asset' | 'Cryptocurrency' | 'AssetIndex' | null => {
    if (value === 'Asset' || value === 'Cryptocurrency' || value === 'AssetIndex') {
        return value;
    }

    return null;
};

const parseLimitFilter = (value: string): JournalLimitFilter => {
    if (value === '50' || value === '100' || value === '200') {
        return Number.parseInt(value, 10) as JournalLimitFilter;
    }

    return 100;
};

const buildDefinitionMeta = (journal: TradeLogicExecutionJournalIndexRecord): React.ReactNode => {
    return (
        <span>
            definition:{' '}
            <span className="text-zinc-300">
                {journal.definition.name} ({journal.definition.kind}) #{journal.definition.id}
            </span>
            {journal.definition.compiledVersion !== null && (
                <span className="ml-1 font-mono text-zinc-500">v{journal.definition.compiledVersion}</span>
            )}
        </span>
    );
};

const TradeLogicExecutionJournals: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [selectedKind, setSelectedKind] = useState<JournalKindFilter>(parseKindFilter(searchParams.get('kind')));
    const [selectedPhase, setSelectedPhase] = useState<JournalPhaseFilter>('all');
    const [selectedStatus, setSelectedStatus] = useState<JournalStatusFilter>('all');
    const [selectedLimit, setSelectedLimit] = useState<JournalLimitFilter>(100);

    const definitionIdParam = parsePositiveInt(searchParams.get('definitionId'));
    const strategyIdParam = parsePositiveInt(searchParams.get('strategyId'));
    const tradeIdParam = parsePositiveInt(searchParams.get('tradeId'));
    const entityIdParam = parsePositiveInt(searchParams.get('entityId'));
    const entityTypeParam = parseEntityType(searchParams.get('entityType'));

    useEffect(() => {
        showSpinner();

        return () => {
            hideSpinner();
        };
    }, []);

    const activeScopeLabels = useMemo(() => {
        const labels: string[] = [];

        if (definitionIdParam !== null) {
            labels.push(`Definition #${definitionIdParam}`);
        }

        if (strategyIdParam !== null) {
            labels.push(`Strategy #${strategyIdParam}`);
        }

        if (tradeIdParam !== null) {
            labels.push(`Trade #${tradeIdParam}`);
        }

        if (entityIdParam !== null && entityTypeParam !== null) {
            labels.push(`${entityTypeParam} #${entityIdParam}`);
        }

        return labels;
    }, [definitionIdParam, entityIdParam, entityTypeParam, strategyIdParam, tradeIdParam]);

    const journalQueryInput = useMemo(
        () => ({
            ...(definitionIdParam !== null ? { definitionId: definitionIdParam } : {}),
            ...(selectedKind !== 'all' ? { kind: selectedKind } : {}),
            ...(selectedPhase !== 'all' ? { phase: selectedPhase } : {}),
            ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
            ...(strategyIdParam !== null ? { strategyId: strategyIdParam } : {}),
            ...(tradeIdParam !== null ? { tradeId: tradeIdParam } : {}),
            ...(entityIdParam !== null ? { entityId: entityIdParam } : {}),
            ...(entityTypeParam !== null ? { entityType: entityTypeParam } : {}),
            limit: selectedLimit
        }),
        [
            definitionIdParam,
            entityIdParam,
            entityTypeParam,
            selectedKind,
            selectedLimit,
            selectedPhase,
            selectedStatus,
            strategyIdParam,
            tradeIdParam
        ]
    );

    const journalsQuery = useAppQuery(
        {
            ...trpc.strategy.fetchTradeLogicExecutionJournalIndex.queryOptions(journalQueryInput)
        },
        {
            fallbackErrorMessage: 'Failed to load Trade Logic activity',
            showErrorToast: true,
            setError: (message) => setError(message)
        }
    );

    const journals = journalsQuery.data?.journals ?? [];

    return (
        <UserLayout title="Trade Logic Activity">
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
                <Card>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                                <h2 className="text-xl font-semibold text-white">Trade Logic V2 Activity</h2>
                                <div className="mt-1 text-sm text-zinc-400">
                                    Recent arbitration and execution journals across the Trade Logic V2 definitions you can access.
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {activeScopeLabels.length > 0 && (
                                    <Link
                                        className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-950/80 px-3 py-2 text-sm font-medium text-zinc-100 hover:border-zinc-500 hover:text-white"
                                        to="/user/tradeLogicExecutionJournals"
                                    >
                                        Clear scoped link
                                    </Link>
                                )}
                            </div>
                        </div>

                        {activeScopeLabels.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {activeScopeLabels.map((label) => (
                                    <span
                                        className="rounded-full border border-zinc-700 bg-zinc-950/80 px-3 py-1 text-xs font-medium text-zinc-300"
                                        key={label}
                                    >
                                        {label}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                            <label className="block">
                                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Logic kind</span>
                                <select
                                    className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                                    value={selectedKind}
                                    onChange={(event) => setSelectedKind(parseKindFilter(event.target.value))}
                                >
                                    {kindFilterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Phase</span>
                                <select
                                    className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                                    value={selectedPhase}
                                    onChange={(event) => setSelectedPhase(parsePhaseFilter(event.target.value))}
                                >
                                    {phaseFilterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Status</span>
                                <select
                                    className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                                    value={selectedStatus}
                                    onChange={(event) => setSelectedStatus(parseStatusFilter(event.target.value))}
                                >
                                    {statusFilterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Window</span>
                                <select
                                    className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                                    value={selectedLimit}
                                    onChange={(event) => setSelectedLimit(parseLimitFilter(event.target.value))}
                                >
                                    {limitFilterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>
                </Card>

                {error && <Alert variant="danger">{error}</Alert>}
                {journalsQuery.isLoading && <DataLoadingSpinner text="Loading Trade Logic activity..." />}
                {!journalsQuery.isLoading && !error && journals.length === 0 && (
                    <Alert variant="warning">No Trade Logic execution journals match the current filters.</Alert>
                )}

                {!journalsQuery.isLoading && journals.length > 0 && (
                    <Card>
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold text-white">Recent Journal Rows</h3>
                                <div className="mt-1 text-sm text-zinc-400">
                                    Every row links to the full trace page and, when possible, the related trade or entry log.
                                </div>
                            </div>
                            <div className="rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs text-zinc-300">
                                {journals.length} rows
                            </div>
                        </div>
                        <div className="space-y-2">
                            {journals.map((journal) => (
                                <TradeLogicExecutionJournalCard
                                    contextMeta={buildDefinitionMeta(journal)}
                                    journal={journal}
                                    key={journal.row.id}
                                />
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        </UserLayout>
    );
};

export default TradeLogicExecutionJournals;
